import { FC } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
// i18next
import { useTranslation } from 'react-i18next';
// Models
import IPage from 'models/Page';
// Types
import QuestionTypes from 'types/QuestionTypes';
// Components
import { Button } from 'components/Buttons';
import Question from './Question';
import MultiLanguageTextField from 'components/MultiLanguageTextField';

type Props = {
  page: IPage;
}

const AdditionalQuestionsForm: FC<Props> = ({ page }) => {
  const { t } = useTranslation();

  const { control, watch, setValue } = useFormContext();

  const watchedPages = watch('pages');
  const pageIndex = watchedPages.findIndex((p: IPage) => p.uuid === page.uuid);

  const { append: appendQuestion, remove: removeQuestion } = useFieldArray({
    control,
    name: 'questions',
  });

  const handleAdd = () => {
    const ref = uuidv4();
    const question: any = {
      ref,
      title: '',
      description: '',
      type: QuestionTypes.SingleChoice,
      image: '',
      video: '',
      answers: [],
    };
    appendQuestion(question);
    setValue(`pages.${pageIndex}.refs`, [...page.refs, ref]);
  };

  const handleRemove = (questionIndex: number, questionRef: string) => {
    removeQuestion(questionIndex);
    setValue(`pages.${pageIndex}.refs`, page.refs.filter(ref => ref !== questionRef));
  };

  return (
    <div className="w-full">
      <span className="block text-gray-400 text-sm font-light leading-5 mb-4">
        {t('pages.quizForm.additionalQuestionsHint')}
      </span>
      <div className="text-right mb-4">
        <Button onClick={handleAdd} variant="contained">{t('buttons.addQuestion')}</Button>
      </div>
      <div className="w-full landscape:w-1/2 tablet:w-full laptop:w-7/12 mb-8">
        <MultiLanguageTextField
          name={`pages.${pageIndex}.title`}
          customKey={`pages.${page.uuid}.title`}
          label={t('labels.title')}
          className="mb-2"
        />
        <span className="block text-gray-400 text-sm font-light leading-5 mb-2">
          {t('form.helperText.enterYourOwnText')}
        </span>
      </div>
      {page.refs.map((ref, index) => (
        <Question
          key={ref}
          questionRef={ref}
          index={index}
          onRemove={page.refs.length > 1 ? handleRemove : undefined}
        />
      ))}
    </div>
  );
};

export default AdditionalQuestionsForm;
